/* eslint-disable ember/no-computed-properties-in-native-classes, ember/require-computed-property-dependencies */
// Extends From
import Controller from '@ember/controller';
// Ember Imports
import { tracked } from '@glimmer/tracking';
import { get, set, action, computed } from '@ember/object';
import { alias } from '@ember/object/computed';
import { service } from '@ember/service';
import { isEmpty } from '@ember/utils';
// Internal Imports
import DataType from '@linc-technologies/linced-common/enums/data-type';
import EnrolmentStatus from 'linced-app/enums/enrolment-status';
import Jurisdiction from '@linc-technologies/linced-common/enums/jurisdiction';
import { filterValue } from '@linc-technologies/linced-common/utils/filtering';
import { findBy } from '@linc-technologies/linced-common/utils/arrays';

export default class AnaphylaxisController extends Controller {
  @service
  session;

  @tracked
  filters = [];

  @alias('session.school.jurisdiction')
  jurisdiction;

  @computed('jurisdiction')
  get isNSWSchool() {
    return this.jurisdiction === Jurisdiction.AU_NSW;
  }

  @computed('model.learners.@each.medicalConditionWithAnaphlyaxis')
  get filteredLearners() {
    return this.model.learners.filter((person) => {
      // Filter out pre-enrols
      let stub = person.learner.enrolmentStub;
      if (!isEmpty(stub)) {
        if (stub.status === EnrolmentStatus.PRE_ENROLLED) {
          return false;
        }
      }

      // Filter to only people with medical conditions with anaphylaxis
      return person.learner.medicalConditionWithAnaphylaxis;
    });
  }

  @computed('filteredLearners')
  get tableData() {
    return this.filteredLearners.map((person) => {
      let hasAnaphlyaxisActionPlan = 'No';
      let autoinjectorMedications = this.model.medications.filter(
        (medication) => {
          return (
            medication.person_id === person.id && medication.isAutoinjector
          );
        },
      );
      person.learner.medicalConditions.forEach((medicalCondition) => {
        let actionPlans = medicalCondition.fields['core:actionPlans'];
        if (
          actionPlans &&
          actionPlans.get('valueList').includes('anaphylaxis')
        ) {
          hasAnaphlyaxisActionPlan = 'Yes';
        }
      });

      let autoinjectorQuantity = 0;
      let autoinjectorLatestExpiry = '';
      autoinjectorMedications.forEach((autoinjector) => {
        autoinjectorQuantity += autoinjector.quantity;
        if (!autoinjector.latestExpiringBatch) {
          return;
        }
        let expiry = autoinjector.latestExpiringBatch.expiryDateString;
        if (autoinjectorLatestExpiry >= expiry) {
          return;
        }
        autoinjectorLatestExpiry = expiry;
      });

      return {
        person: person,
        anaphlyaxisCondition: person.learner.medicalConditionWithAnaphylaxis,
        hasAnaphylaxisActionPlan: hasAnaphlyaxisActionPlan,
        autoinjector: autoinjectorMedications[0],
        autoinjectorQuantity: autoinjectorQuantity,
        autoinjectorLatestExpiry: autoinjectorLatestExpiry,
      };
    });
  }

  @computed('filteredTableData.[]', 'filters.@each.{field,value}')
  get filteredTableData() {
    if (isEmpty(this.filters)) {
      // No filters - just return the data.
      return this.tableData;
    }

    // Determine datatypes for any specified filters.
    let filters = this.filters.map((filter) => {
      return {
        key: filter.field,
        dataType: DataType.STRING,
        value: filter.value,
      };
    });

    // Apply all filters to each row in one hit, to avoid iterating over a
    // (potentially) large dataset multiple times.
    return this.tableData.filter((row) => {
      return filters.every((filter) => {
        // Generic filter processing.
        return filterValue(
          get(row, filter.key) ?? '',
          filter.dataType,
          filter.value,
        );
      });
    });
  }

  @action
  filter(field, value) {
    let filter = findBy(this.filters, 'field', field);
    if (isEmpty(filter)) {
      // No existing filter on this field; add it.
      this.filters = [...this.filters, { field, value }];
      return;
    }

    if (isEmpty(value)) {
      // Filter has been cleared; remove it.
      this.filters = this.filters.filter((f) => f !== filter);
    } else {
      // Existing filter has been changed; update it.
      set(filter, 'value', value);
    }
  }
}
